/**
 * Shared helpers for resolving a merchant-pinned express-wallet processor id (PAY-1701).
 *
 * When a merchant pins a wallet processor via `processorIds.{applePay,googlePay}`, the SDK must
 * route the whole flow (mint + charge) through that processor's REAL family — resolved from the
 * backend's wallet-eligible processors list, never guessed from the scanned default. Shared by the
 * Apple Pay and Google Pay modules so the resolution logic cannot drift between them.
 */

/** A wallet-eligible processor as returned by GET /{apple,google}-pay/processors. */
export type WalletProcessorInfo = { id: string; name: string };

type WalletProcessorsListResponse = { processors: WalletProcessorInfo[] };

/** The per-wallet-module `apiCall` signature (duplicated per file by pre-existing convention). */
type ApiCall = <T>(
  url: string,
  options: RequestInit,
  checkoutRequestId?: string,
) => Promise<{ data?: T; error?: string }>;

/**
 * Build the session-config reader for one wallet module's auto-prepare (PAY-2447).
 *
 * The FIRST read reuses the body of PaymentKit's one shared session fetch (`initialSessionConfig`),
 * so a page with card, Apple Pay and Google Pay makes one GET instead of three, also when the
 * response is slow, up to the per-attempt bound of `fetchSessionConfigWithRetry` (its retry is
 * still the same shared fetch). Past that bound on every attempt, the shared fetch fails and each
 * wallet falls back to its own fetch. Every later read (amount change, shipping toggle, re-prepare after a submit)
 * fetches the endpoint fresh, as does the first read when the shared fetch failed (it resolves to
 * `null`). The shared fetch is `fetchSessionConfigWithRetry` on both init branches, so the first
 * read waits at most its bound (~20s worst case) before it falls back.
 */
export function createWalletSessionConfigReader(initialSessionConfig: Promise<unknown> | undefined) {
  let shared = initialSessionConfig;
  return async <T>(
    apiCall: ApiCall,
    apiBaseUrl: string,
    secureToken: string,
  ): Promise<{ data?: T; error?: string }> => {
    // Cleared before the first await, so a queued or concurrent prepare always fetches fresh.
    const first = shared;
    shared = undefined;
    const initial = await first;
    if (initial && typeof initial === "object") return { data: initial as T };
    return apiCall<T>(`${apiBaseUrl}/api/checkout-sessions/token/${secureToken}`, { method: "GET" });
  };
}

/**
 * Fetch the account's wallet-eligible processors for this checkout session.
 * `slug` is the wallet path segment (`"apple-pay"` | `"google-pay"`).
 * Returns the list, or `null` on a failed fetch (caller treats the wallet as not-ready).
 */
export async function fetchWalletProcessors(
  apiCall: ApiCall,
  apiBaseUrl: string,
  secureToken: string,
  slug: "apple-pay" | "google-pay",
): Promise<WalletProcessorInfo[] | null> {
  const res = await apiCall<WalletProcessorsListResponse>(
    `${apiBaseUrl}/api/checkout/${secureToken}/${slug}/processors`,
    { method: "GET", headers: { "Content-Type": "application/json" } },
  );
  if (res.error || !res.data) {
    console.warn(`[PaymentKit] Auto-prepare: failed to fetch ${slug} processors list:`, res.error);
    return null;
  }
  return res.data.processors ?? [];
}

/**
 * Resolve a pinned processor id's REAL family from the processors list. Returns:
 *  - `"stripe"` / `"airwallex"` for a recognized family,
 *  - `undefined` when the id is absent OR its family is unrecognized (e.g. a future wallet family
 *    added to the backend allowlist).
 *
 * Callers MUST treat `undefined` as "not usable" and fail closed (mark the wallet not-ready) —
 * NEVER default to Stripe, or a future family would silently mis-route into the Stripe flow (the
 * exact bug class PAY-1701 fixes).
 */
export function resolveWalletProcessorType(
  processors: WalletProcessorInfo[],
  pinnedId: string,
): "stripe" | "airwallex" | undefined {
  const entry = processors.find((p) => p.id === pinnedId);
  if (!entry) return undefined;
  if (entry.name === "airwallex") return "airwallex";
  if (entry.name === "stripe") return "stripe";
  return undefined; // unknown family → fail closed, never assume Stripe
}
